import { Block } from './dynamic-artifact.schema';

/**
 * Maximum length for JSON schema property keys imposed by Claude's API
 */
export const MAX_KEY_LENGTH = 64;

/**
 * Suffix pattern used for duplicate keys to maintain consistency with existing behavior
 */
const ELUVE_SUFFIX = '_eluve';

/**
 * Truncates a key to the maximum allowed length for JSON schema property names.
 * This is necessary because Claude has a 64-character limit on property names.
 *
 * @param key - The key to truncate
 * @param maxLength - Maximum length (default: 64 for Claude's limit)
 * @returns Truncated key
 */
export const truncateKey = (
  key: string,
  maxLength = MAX_KEY_LENGTH,
): string => {
  if (key.length <= maxLength) {
    return key;
  }
  return key.substring(0, maxLength);
};

/**
 * Ensures all block keys are within the maximum length limit.
 * If truncation causes duplicate keys, adds suffixes with pattern "_eluve{index}" to maintain uniqueness.
 * This maintains backward compatibility with the existing addEluvePostfix behavior.
 *
 * Note: This modifies the blocks in place using immer's produce pattern.
 *
 * @param blocks - Array of blocks to process
 */
export const ensureUniqueKeys = (blocks: Block[]): void => {
  const keyCount = new Map<string, number>();
  const seenKeys = new Set<string>();

  const processBlock = (block: Block, index: number) => {
    const truncated = truncateKey(block.key);

    // If this is the first time seeing this truncated key, use it as-is
    if (!seenKeys.has(truncated)) {
      block.key = truncated;
      seenKeys.add(truncated);
      keyCount.set(truncated, 1);
    } else {
      // Key collision - add _eluve{index} suffix to maintain consistency with existing behavior
      const suffix = `${ELUVE_SUFFIX}${index}`;
      const maxBaseLength = MAX_KEY_LENGTH - suffix.length;

      // Truncate the base key to make room for suffix
      const uniqueKey = truncated.substring(0, maxBaseLength) + suffix;
      block.key = uniqueKey;
      seenKeys.add(uniqueKey);
    }

    // Recursively process nested group blocks
    if (block.type === 'group') {
      block.blocks.forEach(processBlock);
    }
  };

  blocks.forEach(processBlock);
};
